// 使用jszip
import JSZip from 'jszip'
import axios from 'axios'
import { t } from '@/lang'
import { loadModelFromZip } from '@com/utils/load-model'

import { SkeletonType } from '@com/enums/model'
import { validate3DModel } from '@com/utils/model-file-meta'

// 通过url获得文件数据
export async function gitZipDataByUrl(url: string) {
  const response = await axios({
    method: 'get',
    url: url,
    responseType: 'arraybuffer', // 类型必须为arraybuffer
  })
  const zip = await JSZip.loadAsync(response.data)
  return await getImageByZipFile(zip)
}

// 通过file获得文件数据
export async function gitZipDataByFile(file: File) {
  const zip = await JSZip.loadAsync(file)
  return await getImageByZipFile(zip)
}

interface unzipResult {
  type: string // 类型
  valid: boolean // 检测结果
  message: string // 提示信息
  rule: string // 规则
}

/**
 * 判断zip文件类型
 * zip包内仅包含png文件，图片数量>=1且zip包大小不超过8m时，识别为序列帧；序列帧图片尺寸必须一致，否则报错；
 * zip包含2个json+png文件，zip包大小不超过1m时，识别为龙骨
 * 当解析均不满足以上条件时，为其他zip包
 * @param {File} file 对象
 */

export async function unzip(
  file: any,
  params?: { file_type: 'model' | '3DModel'; skeleton_type?: SkeletonType },
): Promise<unzipResult> {
  const returnInfo: unzipResult = {
    type: 'zip',
    valid: true,
    message: '',
    rule: '',
  }
  // 将zip包文件按照后缀分类
  const extObj: any = {}
  // 解析zip包
  const zip = await JSZip.loadAsync(file.file)
  const files = Object.keys(zip.files)
  const fileDirNames = files.filter((fileName) => {
    return (
      zip.files[fileName]?.dir && !/^(__MACOSX)|(\.DS_Store)/.test(fileName)
    )
  })

  // 存在两个文件夹的情况下为普通zip文件
  if (fileDirNames.length > 1) {
    if (params && params.file_type && params.file_type === 'model') {
      returnInfo.valid = false
      returnInfo.message = t(
        'playTurbo.order.3dmodel.uploader.nosupportFormat',
      ) as string
    }
    return returnInfo
  }
  const fileNamesList = files.filter((fileName) => {
    return (
      !/^(__MACOSX)|(\.DS_Store)/.test(fileName) && !zip.files[fileName]?.dir
    )
  })
  // 一个文件的情况下，并非所有文件都在此文件夹下为普通zip文件
  if (
    fileDirNames.length === 1 &&
    !fileNamesList.every((item) => item.startsWith(fileDirNames[0]!))
  ) {
    return returnInfo
  }

  // 只有一层文件处理
  fileNamesList.forEach((fileName) => {
    const ext = getExt(fileName)
    if (extObj[ext]) {
      extObj[ext].push({
        name: fileName,
      })
    } else if (!extObj[ext]) {
      extObj[ext] = []
      extObj[ext].push({
        name: fileName,
      })
    }
  })

  // eslint-disable-next-line no-prototype-builtins
  if (extObj.hasOwnProperty('png') && Object.keys(extObj).length === 1) {
    returnInfo.type = 'seqframe'
    if (file.size > 8 * 1024 * 1024) {
      returnInfo.valid = false
      returnInfo.message = t('basic.upload.fileStatus.warningXlzSize', {
        size: 8,
      }) as string

      return returnInfo
    }
    const imgInfo: any[] = []
    const data = await Promise.all(
      fileNamesList
        .map((fileName) => zip.file(fileName)!.async('base64'))
        .filter(Boolean),
    )
    for (let i = 0; i < data.length; i++) {
      const imgMsg = await getImgMsg(data[i])
      if (!i) {
        imgInfo.push(imgMsg)
      }
      // 判断序列帧图片尺寸是否一致，尺寸不一致表示序列帧文件异常
      if (
        i &&
        (imgMsg.width !== imgInfo[0].width ||
          imgMsg.height !== imgInfo[0].height)
      ) {
        returnInfo.valid = false
        returnInfo.message = t(
          'basic.upload.sequenceFrameSizeDifferent',
        ) as string
        break
      }
    }
    return returnInfo
  }

  if (
    extObj.hasOwnProperty('png') &&
    extObj.hasOwnProperty('json') &&
    extObj['png'].length === 1 &&
    extObj['json'].length === 2 &&
    Object.keys(extObj).length === 2
  ) {
    returnInfo.type = 'dragonbone'
    if (file.size > 1024 * 1024) {
      returnInfo.valid = false
      returnInfo.message = t('basic.upload.fileStatus.warningDragonboneSize', {
        size: 1,
      }) as string
      return returnInfo
    }
  }
  // 资产库模型校验 校验规则同仓库
  const Model3DTypes = ['fbx', 'glb']
  const texture3DTypes = ['png', 'jpg']
  if (
    Object.keys(extObj).some((ext) =>
      Model3DTypes.includes(String(ext).toLowerCase()),
    ) &&
    Object.keys(extObj).some((ext) =>
      texture3DTypes.includes(String(ext).toLowerCase()),
    ) &&
    params &&
    params.file_type === '3DModel'
  ) {
    let modelInfo = {
      vertices: 0,
      triangles: 0,
      meshes: [],
      material_count: 0,
      skin_count: 0,
      skeleton_names: [],
      skeleton_count: 0,
      has_vertices: false,
      has_normals: false,
      has_UVs: false,
    }
    try {
      modelInfo = await loadModelFromZip(file.file)
    } catch (error) {
      console.log('模型解析失败', error)
    }

    const validateModel = function (modelInfo: any): {
      valid: boolean
      message: string
    } {
      const messages = []
      const {
        material_count,
        meshes,
        skin_count,
        skeleton_names,
        skeleton_count,
        has_vertices,
        has_normals,
        has_UVs,
      } = modelInfo
      // 顶点、法线、纹理坐标都为true;
      if (!has_vertices || !has_normals || !has_UVs) {
        messages.push(t('basic.upload.fileStatus.verticesRules'))
      }
      //  模型网格数量 = 1;
      if (meshes.length !== 1) {
        messages.push(t('basic.upload.fileStatus.meshesRules'))
      }
      //   材质数量=1
      if (material_count !== 1) {
        messages.push(t('basic.upload.fileStatus.materialRules'))
      }

      if (
        params.skeleton_type &&
        [
          SkeletonType.SNAKE,
          SkeletonType.SIMPLE,
          SkeletonType.FISH,
          SkeletonType.HUMANOID,
          SkeletonType.REPTILIA,
        ].indexOf(params.skeleton_type) !== -1
      ) {
        if (skin_count !== 1) {
          messages.push(t('basic.upload.fileStatus.skinRules'))
        }
        if (!validate3DModel(params.skeleton_type, skeleton_names)) {
          messages.push(t('basic.upload.fileStatus.skeletonRules'))
        }
      } else if (
        params.skeleton_type &&
        [SkeletonType.BIRD].indexOf(params.skeleton_type) !== -1
      ) {
        if (skin_count !== 1) {
          messages.push(t('basic.upload.fileStatus.skinRules'))
        }
      }
      const valid = !messages.length
      const message = messages.length
        ? t('playTurbo.order.3dmodel.uploader.warning.contentError') +
          messages.join('，') +
          '。'
        : ''

      return {
        valid,
        message,
      }
    }
    returnInfo.type = 'model'
    const validRes = validateModel(modelInfo)
    returnInfo.valid = validRes.valid
    returnInfo.message = validRes.message
    return returnInfo
  }

  // 工单模型校验
  // 模型文件， 需要包含模型文件和贴图文件，并且贴图文件只能有一个
  const modelTypes = ['fbx', 'ma', 'mb', 'max', 'glb', 'gltf']
  const textureTypes = ['png', 'jpg', 'jpeg']
  if (
    Object.keys(extObj).some((ext) =>
      modelTypes.includes(String(ext).toLowerCase()),
    ) &&
    Object.keys(extObj).some((ext) =>
      textureTypes.includes(String(ext).toLowerCase()),
    ) &&
    params &&
    params.file_type === 'model'
  ) {
    // 默认都是能通过检验的, 三角面数< 10000，顶点数 < 8000，Mesh = 1 才校验通过
    let modelInfo = {
      vertices: 0,
      triangles: 0,
      meshes: [1],
    }
    try {
      modelInfo = await loadModelFromZip(file.file)
    } catch (error) {
      console.log('模型解析失败', error)
    }
    console.log('当前模型的信息', modelInfo)

    const validateModel = function (modelInfo): {
      valid: boolean
      message: string
    } {
      const { triangles, meshes } = modelInfo
      const messages = []
      const isTrianglesValid = triangles < 10000
      if (!isTrianglesValid) {
        messages.push(t('playTurbo.order.3dmodel.uploader.warning.triangles'))
      }
      const isVerticesValid = true // 顶点数由于 web 端获取不准确，暂时不校验
      if (!isVerticesValid) {
        messages.push(t('playTurbo.order.3dmodel.uploader.warning.vertex'))
      }
      const isMeshValid = meshes.length === 1
      if (!isMeshValid) {
        messages.push(t('playTurbo.order.3dmodel.uploader.warning.mesh'))
      }
      const valid = isTrianglesValid && isVerticesValid && isMeshValid
      const message = messages.length
        ? t('playTurbo.order.3dmodel.uploader.warning.contentError') +
          messages.join('，') +
          '。'
        : ''
      return {
        valid,
        message,
      }
    }

    returnInfo.type = 'model'
    // 有一个png或者jpg或者jpeg文件
    if (
      (extObj['png']?.length || 0) +
        (extObj['jpg']?.length || 0) +
        (extObj['jpeg']?.length || 0) >=
      1
    ) {
      const validRes = validateModel(modelInfo)
      returnInfo.valid = validRes.valid
      returnInfo.message = validRes.message
      return returnInfo
    }
    returnInfo.valid = false
    returnInfo.message = t(
      'playTurbo.order.3dmodel.uploader.warning.picCountError',
    ) as string
    return returnInfo
  } else if (
    Object.keys(extObj).some((ext) =>
      modelTypes.includes(String(ext).toLowerCase()),
    ) &&
    params &&
    params.file_type === 'model'
  ) {
    // 工单模型校验 无贴图情况
    returnInfo.type = 'model'
    returnInfo.valid = false
    returnInfo.message = t(
      'playTurbo.order.3dmodel.uploader.warning.picCountError',
    ) as string
  }
  return returnInfo
}

// 获得图片的宽高
export function getImgMsg(
  file: any,
): Promise<{ width: number; height: number }> {
  return new Promise((resovle) => {
    const img = new Image()
    img.src = `data:image/jpeg;base64,${file}`
    img.onload = async function () {
      resovle({
        width: img.width,
        height: img.height,
      })
    }
  })
}

// 获得文件后缀
function getExt(n: string): string {
  if (n.indexOf('.') < 0) {
    return n
  }
  return n.slice(n.lastIndexOf('.') + 1)
}

// 处理解析后zip文件数据
async function getImageByZipFile(zip: any) {
  const fileNameList = Object.keys(zip.files).sort((a, b) => {
    return Number(a.replace(/[^\d]/g, '')) - Number(b.replace(/[^\d]/g, ''))
  })
  const fileNames = fileNameList.filter((fileName) => {
    return (
      !/^(__MACOSX)|(\.DS_Store)/.test(fileName) && !zip.files[fileName]?.dir
    )
  })
  const data = await Promise.all(
    fileNames
      .map((fileName) => zip.file(fileName)!.async('base64'))
      .filter(Boolean),
  )
  for (let i = 0; i < data.length; i++) {
    data[i] = `data:image/jpeg;base64,${data[i]}`
  }
  return data
}
